import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import { Divider, Typography } from "@mui/material";
import Autocomplete from "@components/Autocomplete/Autocomplete";
import Button from "@components/Button/Button";
import BaseTable from "@components/DataTable/BaseTable";
import type { Header } from "@components/DataTable/CustomDataTable.types.ts";
import CustomFormLabel from "@components/FormItem/FormItem.styles";
import Input from "@components/Input/Input";
import SideDrawer from "@components/SimpleSideDrawer/SideDrawer";
import type { AddPortServiceOutlet } from "@features/vessel-call/vessel-call-status/PortServices/form/AddPortService.tsx";
import {
  getWasteFormSchema,
  type WasteFormSchema,
} from "@features/vessel-call/vessel-call-status/PortServices/schema/portServicesFormSchema";
import {
  usePortServicesFormStore,
  type MarpolAnnexI,
} from "@features/vessel-call/vessel-call-status/PortServices/store/usePortServicesForm.store.ts";
import { zodResolver } from "@hookform/resolvers/zod";
import type { TFunction } from "i18next";
import { Controller, useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useOutletContext } from "react-router-dom";

const TABLE_FIELDS = Object.freeze({
  marpolAnnexIOil: "marpolAnnexIOil",
  quantityM3: "quantityM3",
  action: "action",
});

const LANG_BASE = "vessel-call.marpol-annex1";
const LANG_BASE_HEADER = `${LANG_BASE}.grid-headers`;
const LANG_BASE_SIDEBAR = `${LANG_BASE}.sidebar`;
const TEST_ID_BASE = "marpol-annex";
const OTHER_SPECIFY_MAX_CHARS = 250;

export const getMarpolOptions = (t: TFunction) => ({
  sludge: [
    { value: "Oily residues (sludges)", label: t(`${LANG_BASE}.sludge.oilyResidues`) },
    {
      value: "Scale and sludge from tank cleaning",
      label: t(`${LANG_BASE}.sludge.scaleAndSludge`),
    },
    { value: "Other (Specify)", label: t(`${LANG_BASE}.otherSpecify`) },
  ],
  slops: [
    { value: "Oily bilge water", label: t(`${LANG_BASE}.slops.oilyBilgeWater`) },
    { value: "Oily tank washings", label: t(`${LANG_BASE}.slops.oilyTankWashings`) },
    { value: "Dirty ballast water", label: t(`${LANG_BASE}.slops.dirtyBallastWater`) },
    { value: "Other (Specify)", label: t(`${LANG_BASE}.otherSpecify`) },
  ],
});

function buildWasteDefaults(src?: Partial<WasteFormSchema>) {
  return {
    marpolAnnexIOil: src?.marpolAnnexIOil ?? "",
    otherSpecifyDetails: src?.otherSpecifyDetails ?? "",
    quantityM3: src?.quantityM3 ?? 0,
  } as const;
}

const MarpolAnnexGrid: React.FC = () => {
  const { t } = useTranslation();
  const {
    wasteResiduesForDischargeFacility: persistedWastes,
    addWasteDraft,
    setWaste,
    setAddWasteDraft,
  } = usePortServicesFormStore();
  const { formData } = useOutletContext<AddPortServiceOutlet>();

  const { sludge, slops } = getMarpolOptions(t);

  const MARPOL_OPTIONS = formData.getValues().serviceSubType === "Slops" ? slops : sludge;

  const wasteForm = useForm<WasteFormSchema>({
    resolver: zodResolver(getWasteFormSchema(t)),
    defaultValues: buildWasteDefaults(),
    mode: "onChange",
  });

  const watchedWastes = useWatch({
    control: formData.control,
    name: "wasteResiduesForDischargeFacility",
  });
  const wasteDraft = useWatch({ control: wasteForm.control });
  const selectedWasteMarpolAnnexIOil = useWatch({
    control: wasteForm.control,
    name: "marpolAnnexIOil",
  });

  const wastes = watchedWastes ?? persistedWastes;

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [editingWaste, setEditingWaste] = useState<MarpolAnnexI | null>(null);
  const previousIsDrawerOpenRef = useRef(false);
  const previousWasteMarpolAnnexIOilRef = useRef<string | undefined>(undefined);
  const lastPersistedDraftRef = useRef<string>("");

  const isEditing = editingWaste !== null;
  const isOtherSpecifySelected = selectedWasteMarpolAnnexIOil === "Other (Specify)";

  const headers: Header[] = useMemo(
    () => [
      {
        field: TABLE_FIELDS.marpolAnnexIOil,
        columnName: t(`${LANG_BASE_HEADER}.marpolAnnexIOil`),
      },
      {
        field: TABLE_FIELDS.quantityM3,
        columnName: t(`${LANG_BASE_HEADER}.quantityM3`),
      },
      {
        field: TABLE_FIELDS.action,
        columnName: t(`${LANG_BASE_HEADER}.action`),
      },
    ],
    [t],
  );

  const tableRows = useMemo(() => {
    return (wastes ?? []).map((waste, idx) => ({
      id: `waste-${idx}`,
      ...waste,
      marpolAnnexIOil:
        waste.marpolAnnexIOil === "Other (Specify)"
          ? (waste.otherSpecifyDetails ?? "")
          : waste.marpolAnnexIOil,
    }));
  }, [wastes]);

  const fieldErrorMessage = (
    formData.formState.errors.wasteResiduesForDischargeFacility as unknown as { message?: string }
  )?.message;

  const openAddDrawer = useCallback(() => {
    setEditingWaste(null);
    setIsDrawerOpen(true);
  }, []);

  const openEditDrawer = useCallback(
    (rowIndex: number) => {
      const row = wastes?.[rowIndex];
      if (!row) return;
      setEditingWaste(row);
      setIsDrawerOpen(true);
    },
    [wastes],
  );

  const closeDrawer = useCallback(() => {
    setIsDrawerOpen(false);
  }, []);

  useEffect(() => {
    const hasJustOpened = isDrawerOpen && !previousIsDrawerOpenRef.current;
    previousIsDrawerOpenRef.current = isDrawerOpen;

    if (!hasJustOpened) return;

    const initialValues = isEditing
      ? {
          marpolAnnexIOil: editingWaste?.marpolAnnexIOil ?? "",
          otherSpecifyDetails: editingWaste?.otherSpecifyDetails ?? "",
          quantityM3: editingWaste?.quantityM3 ?? 0,
        }
      : buildWasteDefaults(addWasteDraft ?? undefined);

    wasteForm.reset(initialValues);
    previousWasteMarpolAnnexIOilRef.current = undefined;
  }, [addWasteDraft, editingWaste, isDrawerOpen, isEditing, wasteForm]);

  useEffect(() => {
    if (!isDrawerOpen) return;
    if (isEditing) return;

    const normalizedDraft = buildWasteDefaults(wasteDraft as Partial<WasteFormSchema>);
    const nextSignature = JSON.stringify(normalizedDraft);

    if (lastPersistedDraftRef.current === nextSignature) return;

    lastPersistedDraftRef.current = nextSignature;
    setAddWasteDraft(normalizedDraft);
  }, [isDrawerOpen, isEditing, setAddWasteDraft, wasteDraft]);

  useEffect(() => {
    if (!isDrawerOpen) {
      previousWasteMarpolAnnexIOilRef.current = undefined;
      return;
    }

    const previousValue = previousWasteMarpolAnnexIOilRef.current;
    previousWasteMarpolAnnexIOilRef.current = selectedWasteMarpolAnnexIOil;

    const transitionedAwayFromOtherSpecify =
      previousValue === "Other (Specify)" && selectedWasteMarpolAnnexIOil !== "Other (Specify)";

    if (!transitionedAwayFromOtherSpecify) return;

    const currentOtherSpecifyDetails = wasteForm.getValues("otherSpecifyDetails");

    if (!currentOtherSpecifyDetails) {
      wasteForm.clearErrors("otherSpecifyDetails");
      return;
    }

    wasteForm.setValue("otherSpecifyDetails", "", {
      shouldDirty: true,
      shouldValidate: true,
    });
    wasteForm.clearErrors("otherSpecifyDetails");
  }, [isDrawerOpen, selectedWasteMarpolAnnexIOil, wasteForm]);

  const syncWastes = useCallback(
    (next: MarpolAnnexI[]) => {
      setWaste(next);
      formData.setValue("wasteResiduesForDischargeFacility", next, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      });
    },
    [formData, setWaste],
  );
  const maximumWasteReached = wastes && wastes.length >= 15;
  const onRowDelete = useCallback(
    (row: { id: string }) => {
      const idx = Number(row.id.replace("waste-", ""));
      if (Number.isNaN(idx)) return;
      const next = (wastes ?? []).filter((_, i) => i !== idx);
      syncWastes(next);
    },
    [syncWastes, wastes],
  );

  const onRowEdit = useCallback(
    (row: { id: string }) => {
      const idx = Number(row.id.replace("waste-", ""));
      if (Number.isNaN(idx)) return;
      openEditDrawer(idx);
    },
    [openEditDrawer],
  );
  const handlePersistWasteDraft = useCallback(
    (draft: Partial<WasteFormSchema> | null) => {
      if (!editingWaste) {
        setAddWasteDraft(draft);
      }
    },
    [editingWaste, setAddWasteDraft],
  );

  const handleSubmitDrawer = useCallback(
    (submittedWaste?: Partial<WasteFormSchema>) => {
      if (!submittedWaste) return;

      const parsedQuantity = Number(submittedWaste.quantityM3);

      const waste: MarpolAnnexI = {
        marpolAnnexIOil: submittedWaste.marpolAnnexIOil ?? "",
        otherSpecifyDetails: submittedWaste.otherSpecifyDetails ?? "",
        quantityM3: Number.isFinite(parsedQuantity) ? parsedQuantity : 0,
      };

      if (isEditing && editingWaste != null) {
        const next = (wastes ?? []).map((w) => (w === editingWaste ? waste : w));
        syncWastes(next);
        setEditingWaste(null);
        setIsDrawerOpen(false);
        return;
      }

      syncWastes([...(wastes ?? []), waste]);
      setIsDrawerOpen(false);
    },
    [editingWaste, isEditing, syncWastes, wastes],
  );

  const handleCancelDrawer = useCallback(() => {
    if (!isEditing) {
      handlePersistWasteDraft(null);
    }
    closeDrawer();
  }, [closeDrawer, handlePersistWasteDraft, isEditing]);

  const handleDrawerSubmit = useCallback(() => {
    const wasteValues = wasteForm.getValues();
    const result = getWasteFormSchema(t).safeParse(wasteValues);

    if (!result.success) {
      wasteForm.trigger();
      return;
    }

    handleSubmitDrawer(wasteValues);
    setAddWasteDraft(null);
    wasteForm.reset(buildWasteDefaults());
  }, [handleSubmitDrawer, setAddWasteDraft, t, wasteForm]);

  return (
    <div className="flex flex-col items-end gap-4" data-testid={TEST_ID_BASE}>
      <div className="flex flex-col w-full gap-4">
        <Typography variant="subtitle1">{t(`${LANG_BASE}.title`)}</Typography>
        <BaseTable
          rows={tableRows}
          headers={headers}
          onRowEdit={onRowEdit}
          onRowDelete={onRowDelete}
        />
      </div>

      <Divider className="w-full" />

      <div className="flex w-full justify-between items-center">
        <Typography color="error" variant="caption">
          {fieldErrorMessage && String(fieldErrorMessage)}
        </Typography>

        {!maximumWasteReached && (
          <Button
            type="button"
            variant="outlined"
            color="primary"
            testId="page-header-register-vessel-button"
            label={
              <span className="text-sm font-medium flex items-center gap-2">
                <AddCircleOutlineIcon fontSize="small" />
                {t(`${LANG_BASE}.button`)}
              </span>
            }
            onClick={openAddDrawer}
            sx={{ textTransform: "none", height: "40px", minHeight: "40px" }}
          />
        )}
      </div>

      {isDrawerOpen && (
        <SideDrawer
          isOpen={isDrawerOpen}
          onClose={closeDrawer}
          onCancel={handleCancelDrawer}
          onSubmit={handleDrawerSubmit}
          title={t(`${LANG_BASE_SIDEBAR}.${isEditing ? "title-edit" : "title-add"}`)}
          submitLabel={t(`${LANG_BASE_SIDEBAR}.buttons.${isEditing ? "edit" : "add"}`)}
        >
          <div className="flex flex-col gap-3 px-6">
            <Controller
              name="marpolAnnexIOil"
              control={wasteForm.control}
              render={({ field }) => (
                <div className="label-and-input flex flex-col gap-2">
                  <CustomFormLabel data-testid="labelId">
                    {t("vessel-call.marpol-annex1.grid-headers.marpolAnnexIOil")}
                  </CustomFormLabel>
                  <Autocomplete
                    options={MARPOL_OPTIONS}
                    value={
                      field.value
                        ? (MARPOL_OPTIONS.find((o) => String(o.value) === String(field.value)) ??
                          null)
                        : null
                    }
                    onChange={(opt) => {
                      const nextValue = opt?.value == null ? "" : String(opt.value);
                      field.onChange(nextValue);

                      if (nextValue !== "Other (Specify)") {
                        wasteForm.setValue("otherSpecifyDetails", "", {
                          shouldDirty: true,
                          shouldTouch: true,
                          shouldValidate: true,
                        });
                        wasteForm.clearErrors("otherSpecifyDetails");
                      }

                      if (opt?.value == null) {
                        return;
                      }
                    }}
                    testId={`${TEST_ID_BASE}-waste-marpolAnnexIOil-input`}
                    placeholder={t("vessel-call.marpol-annex1.grid-headers.marpolAnnexIOil")}
                    maxHeight={200}
                    error={!!wasteForm.formState.errors.marpolAnnexIOil}
                  />
                  {wasteForm.formState.errors.marpolAnnexIOil && (
                    <Typography color="error" variant="caption">
                      {wasteForm.formState.errors.marpolAnnexIOil.message}
                    </Typography>
                  )}
                </div>
              )}
            />

            {isOtherSpecifySelected && (
              <div className="label-and-input flex flex-col gap-2">
                <CustomFormLabel data-testid="labelId">
                  {t("vessel-call.marpol-annex1.grid-headers.otherSpecify")}
                </CustomFormLabel>
                <Input
                  error={!!wasteForm.formState.errors.otherSpecifyDetails}
                  placeholder={t("vessel-call.marpol-annex1.grid-headers.otherSpecify")}
                  id={`${TEST_ID_BASE}-waste-otherSpecify-input`}
                  value={wasteForm.watch("otherSpecifyDetails")}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value.length > OTHER_SPECIFY_MAX_CHARS) {
                      wasteForm.setValue(
                        "otherSpecifyDetails",
                        value.slice(0, OTHER_SPECIFY_MAX_CHARS),
                        { shouldDirty: true, shouldTouch: true },
                      );
                      wasteForm.setError("otherSpecifyDetails", {
                        type: "maxLength",
                        message: t("form-validation.max-length", {
                          label: t("vessel-call.marpol-annex1.sidebar.fields.otherSpecify"),
                          max: OTHER_SPECIFY_MAX_CHARS,
                        }),
                      });
                      return;
                    }
                    wasteForm.setValue("otherSpecifyDetails", value, {
                      shouldDirty: true,
                      shouldTouch: true,
                      shouldValidate: true,
                    });
                  }}
                  testId={`${TEST_ID_BASE}-waste-otherSpecify-input`}
                  style={
                    wasteForm.formState.errors.otherSpecifyDetails ? { borderColor: "#d32f2f" } : {}
                  }
                />
                {wasteForm.formState.errors.otherSpecifyDetails && (
                  <Typography color="error" variant="caption">
                    {wasteForm.formState.errors.otherSpecifyDetails.message}
                  </Typography>
                )}
              </div>
            )}

            <div className="label-and-input flex flex-col gap-2">
              <CustomFormLabel data-testid="labelId">
                {t("vessel-call.marpol-annex1.grid-headers.quantityM3")}
              </CustomFormLabel>
              <Input
                type="text"
                error={!!wasteForm.formState.errors.quantityM3}
                placeholder={t("vessel-call.marpol-annex1.grid-headers.quantityM3")}
                id={`${TEST_ID_BASE}-waste-quantityM3-input`}
                value={String(wasteForm.watch("quantityM3") ?? "")}
                slotProps={{ htmlInput: { inputMode: "numeric", pattern: "[0-9]*", maxLength: 9 } }}
                onChange={(e) => {
                  const digitsOnly = e.target.value.replace(/\D/g, "").slice(0, 9);
                  const next = digitsOnly === "" ? 0 : Number(digitsOnly);
                  wasteForm.setValue("quantityM3", next, {
                    shouldDirty: true,
                    shouldTouch: true,
                    shouldValidate: true,
                  });
                }}
                testId={`${TEST_ID_BASE}-waste-quantityM3-input`}
                style={wasteForm.formState.errors.quantityM3 ? { borderColor: "#d32f2f" } : {}}
              />
              {wasteForm.formState.errors.quantityM3 && (
                <Typography color="error" variant="caption">
                  {wasteForm.formState.errors.quantityM3.message}
                </Typography>
              )}
            </div>
          </div>
        </SideDrawer>
      )}
    </div>
  );
};

export default MarpolAnnexGrid;
